import { log } from '@/log'
import { localStorageKeys } from '@/registry/local-storage-keys'
import {
  GpPromotionContext,
  GpPromotionContextUserStatus,
} from '@/vendor/gtm/events/gp/promotion-context'

interface PromotionContextCacheDependency {
  readonly storage: Storage
  readonly getNow: () => number
}

interface CacheEntry {
  readonly expiresAt: number
  readonly context: GpPromotionContext
}

// tslint:disable-next-line:no-magic-numbers
const MINUTE_IN_MS = 60 * 1000
const DEFAULT_TTL_IN_MINUTES = 10
const STORAGE_KEY = localStorageKeys.promotionContext

const defaultDependency = (): PromotionContextCacheDependency => ({
  storage: localStorage,
  getNow: () => Date.now(),
})

const isObject = (value: unknown): value is { [key: string]: unknown } =>
  typeof value === 'object' && value !== null

const isString = (value: unknown): value is string =>
  typeof value === 'string'

const isNumber = (value: unknown): value is number =>
  typeof value === 'number'

const isUserStatus = (value: unknown): value is GpPromotionContextUserStatus =>
  value === 'registered' || value === 'provisional'

const isPromotionContext = (value: unknown): value is GpPromotionContext => {
  if (!isObject(value)) return false

  const user = value.user
  const order = value.order
  const point = value.point

  if (!isObject(user) || !isObject(order) || !isObject(point)) return false

  return isString(user.id)
    && isUserStatus(user.status)
    && isNumber(order.order_times)
    && (order.last_order_date === null || isString(order.last_order_date))
    && isNumber(point.total_amount)
    && (point.next_expiring_amount === null || isNumber(point.next_expiring_amount))
    && (point.next_expiring_date === null || isString(point.next_expiring_date))
}

const isCacheEntry = (value: unknown): value is CacheEntry =>
  isObject(value)
  && isNumber(value.expiresAt)
  && isPromotionContext(value.context)

export class PromotionContextCache {
  static readonly storageKey = STORAGE_KEY
  static readonly defaultTtlMs = DEFAULT_TTL_IN_MINUTES * MINUTE_IN_MS

  static createDefault(): PromotionContextCache {
    return new PromotionContextCache()
  }

  constructor(
    private readonly dependency: PromotionContextCacheDependency = defaultDependency(),
    private readonly ttlMs: number = PromotionContextCache.defaultTtlMs
  ) {}

  get(userId: string): GpPromotionContext | null {
    const entry = this.readEntry()
    if (entry === null) return null

    if (entry.expiresAt <= this.dependency.getNow()) {
      this.removeEntry()

      return null
    }

    if (entry.context.user.id !== userId) {
      this.removeEntry()

      return null
    }

    return entry.context
  }

  set(context: GpPromotionContext): void {
    this.writeEntry({
      expiresAt: this.dependency.getNow() + this.ttlMs,
      context,
    })
  }

  private readEntry(): CacheEntry | null {
    try {
      const raw = this.dependency.storage.getItem(STORAGE_KEY)
      if (raw === null) return null

      const parsed = JSON.parse(raw)
      if (!isCacheEntry(parsed)) {
        throw new Error('Invalid promotion context cache format')
      }

      return parsed
    } catch (error) {
      log.error(error)
      this.removeEntry()

      return null
    }
  }

  private writeEntry(entry: CacheEntry): void {
    try {
      this.dependency.storage.setItem(STORAGE_KEY, JSON.stringify(entry))
    } catch (error) {
      log.error(error)
    }
  }

  private removeEntry(): void {
    try {
      this.dependency.storage.removeItem(STORAGE_KEY)
    } catch (error) {
      log.error(error)
    }
  }
}
